import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import MainLayout from '../components/Layout/MainLayout';
import adminService from '../services/adminService';
import { useAuth } from '../contexts/AuthContext';
import { STATES, getDistrictsByState } from '../constants/locations';
import api from '../services/api';

const CreateUserPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [salaryPreview, setSalaryPreview] = useState(null);
  const [formData, setFormData] = useState({
    // Basic Information
    name: '',
    fatherName: '',
    passportNumber: '',
    gender: '',
    mobile: '',
    email: '',
    dateOfBirth: '',
    bloodGroup: '',
    emergencyNumber: '',
    role: 'HR',
    state: '',
    district: '',
    currentJobRole: '',
    workExperience: '',
    education: '',
    place: '',
    joiningDate: '',
    
    // Bank Details (for HR/HRO only)
    accountHolderName: '',
    accountNumber: '',
    ifscCode: '',
    bankName: '',
    
    // Salary Structure (for HR/HRO only)
    basic: '',
    da: '',
    hra: '',
    specialAllowance: '',
    
    // Deductions (for HR/HRO only)
    pf: '',
    tds: '',
    esi: '',
    professionalTax: '',
    otherDeduction: '',
    otherDeductionDescription: ''
  });

  const [files, setFiles] = useState({
    resume: null,
    joiningLetter: null,
    bankDocument: null,
    additionalDocs: []
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Handle mobile number validation (only digits, max 10)
    if (name === 'mobile') {
      const digitsOnly = value.replace(/\D/g, '');
      if (digitsOnly.length <= 10) {
        setFormData(prev => ({
          ...prev,
          [name]: digitsOnly
        }));
      }
      return;
    }

    // Handle IFSC code formatting
    if (name === 'ifscCode') {
      setFormData(prev => ({
        ...prev,
        [name]: value.toUpperCase()
      }));
      return;
    }

    // Handle state change - reset district when state changes
    if (name === 'state') {
      setFormData(prev => ({
        ...prev,
        state: value,
        district: '' // Reset district when state changes
      }));
      return;
    }

    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Auto-calculate salary preview when salary or deduction fields change
    if (['basic', 'da', 'hra', 'specialAllowance', 'pf', 'tds', 'esi', 'professionalTax', 'otherDeduction'].includes(name)) {
      const updatedFormData = { ...formData, [name]: value };
      calculateSalaryPreview(updatedFormData);
    }
  };

  const handleFileChange = (e, fileType) => {
    const selectedFiles = Array.from(e.target.files);
    
    if (fileType === 'additionalDocs') {
      if (selectedFiles.length > 5) {
        toast.error('Maximum 5 additional documents allowed');
        return;
      }
      setFiles(prev => ({
        ...prev,
        [fileType]: selectedFiles
      }));
    } else {
      setFiles(prev => ({
        ...prev,
        [fileType]: selectedFiles[0]
      }));
    }
  };

  const calculateSalaryPreview = async (data = formData) => {
    if (!data.basic || !['HR', 'HRO'].includes(data.role)) return;

    try {
      const response = await api.post('/admin/calculate-salary', {
        basic: parseFloat(data.basic) || 0,
        da: parseFloat(data.da) || 0,
        hra: parseFloat(data.hra) || 0,
        specialAllowance: parseFloat(data.specialAllowance) || 0,
        pf: parseFloat(data.pf) || 0,
        tds: parseFloat(data.tds) || 0,
        esi: parseFloat(data.esi) || 0,
        professionalTax: parseFloat(data.professionalTax) || 0,
        otherDeduction: parseFloat(data.otherDeduction) || 0
      });

      if (response.data.success) {
        setSalaryPreview(response.data.salary);
      }
    } catch (error) {
      console.error('Salary calculation error:', error);
    }
  };

  const validateForm = () => {
    // Basic required fields for all roles
    const basicRequiredFields = ['name', 'gender', 'mobile', 'email', 'dateOfBirth', 'role'];
    
    // Additional required fields for HR/HRO
    const hrRequiredFields = ['fatherName', 'joiningDate'];
    
    // Passport required for CANDIDATE
    if (formData.role === 'CANDIDATE') {
      basicRequiredFields.push('passportNumber');
    }

    // Check basic required fields
    const missingBasicFields = basicRequiredFields.filter(field => !formData[field]);
    if (missingBasicFields.length > 0) {
      toast.error(`Missing required fields: ${missingBasicFields.join(', ')}`);
      return false;
    }

    // Check HR/HRO specific fields
    if (['HR', 'HRO'].includes(formData.role)) {
      const missingHRFields = hrRequiredFields.filter(field => !formData[field]);
      if (missingHRFields.length > 0) {
        toast.error(`Missing required HR fields: ${missingHRFields.join(', ')}`);
        return false;
      }

      // File validation for HR/HRO
      if (!files.resume) {
        toast.error('Resume file is required for HR/HRO roles');
        return false;
      }

      if (!files.joiningLetter) {
        toast.error('Joining letter file is required for HR/HRO roles');
        return false;
      }

      if (formData.ifscCode) {
        const ifscRegex = /^[A-Z]{4}0[A-Z0-9]{6}$/;
        if (!ifscRegex.test(formData.ifscCode)) {
          toast.error('Invalid IFSC code format');
          return false;
        }
      }

      if (formData.accountNumber) {
        const accountRegex = /^\d{9,18}$/;
        if (!accountRegex.test(formData.accountNumber)) {
          toast.error('Account number should be 9-18 digits');
          return false;
        }
      }

      if (formData.basic && parseFloat(formData.basic) <= 0) {
        toast.error('Basic salary must be greater than 0');
        return false;
      }
    }

    // Phone number validation
    if (formData.mobile.length !== 10) {
      toast.error('Mobile number must be exactly 10 digits');
      return false;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error('Invalid email format');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setLoading(true);

    try {
      let result;

      // Use different endpoints based on role
      if (['HR', 'HRO'].includes(formData.role)) {
        // Use complete employee creation for HR/HRO with files
        const submitFormData = new FormData();
        
        // Add form fields
        const submitData = { ...formData };
        if (['HR', 'HRO'].includes(submitData.role) && !submitData.currentJobRole) {
          submitData.currentJobRole = submitData.role === 'HR' ? 'Human Resource Representative' : 'HR Officer';
        }

        Object.keys(submitData).forEach(key => {
          if (submitData[key]) {
            const fieldName = key === 'mobile' ? 'phoneNumber' : key;
            submitFormData.append(fieldName, submitData[key]);
          }
        });

        // Add files
        if (files.resume) {
          submitFormData.append('resume', files.resume);
        }
        if (files.joiningLetter) {
          submitFormData.append('joiningLetter', files.joiningLetter);
        }
        if (files.bankDocument) {
          submitFormData.append('bankDocument', files.bankDocument);
        }
        if (files.additionalDocs.length > 0) {
          files.additionalDocs.forEach(doc => {
            submitFormData.append('additionalDocs', doc);
          });
        }

        const response = await api.post('/admin/create-complete-user', submitFormData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });

        result = response.data;
      } else {
        // Use basic user creation for other roles
        result = await adminService.createUser(formData);
      }
      
      if (result.success) {
        toast.success(result.message);
        
        // Show credentials for HR/HRO users
        if (result.credentials) {
          toast.info(`Employee ID: ${result.credentials.employeeId}, Password: ${result.credentials.password}`, {
            autoClose: 10000
          });
        }
        
        navigate('/admin/users');
      } else {
        toast.error(result.error);
      }
    } catch (error) {
      console.error('Create user error:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to create user';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      fatherName: '',
      passportNumber: '',
      gender: '',
      mobile: '',
      email: '',
      dateOfBirth: '',
      bloodGroup: '',
      emergencyNumber: '',
      role: 'HR',
      state: '',
      district: '',
      currentJobRole: '',
      workExperience: '',
      education: '',
      place: '',
      joiningDate: '',
      accountHolderName: '',
      accountNumber: '',
      ifscCode: '',
      bankName: '',
      basic: '',
      da: '',
      hra: '',
      specialAllowance: '',
      pf: '',
      tds: '',
      esi: '',
      professionalTax: '',
      otherDeduction: '',
      otherDeductionDescription: ''
    });
    setFiles({
      resume: null,
      joiningLetter: null,
      bankDocument: null,
      additionalDocs: []
    });
    setSalaryPreview(null);
  };

  // Get districts for selected state
  const availableDistricts = formData.state ? getDistrictsByState(formData.state) : [];

  const canAccessUserCreation = ['ADMIN', 'CHAIRMAN'].includes(user?.role);

  // Check if user can access user creation
  if (!canAccessUserCreation) {
    return (
      <MainLayout>
        <div className="max-w-4xl mx-auto p-6">
          <div className="text-center py-12">
            <h2 className="text-xl font-semibold text-gray-600">Access Denied</h2>
            <p className="text-gray-500 mt-2">This page is only accessible to Admin and Chairman users.</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="max-w-4xl mx-auto p-6">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Create New User</h1>
          <p className="text-gray-600">Create comprehensive user accounts with complete profiles for all roles</p>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Role Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Role <span className="text-red-500">*</span>
              </label>
              <select
                name="role"
                value={formData.role}
                onChange={handleInputChange}
                required
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="HR">HR Employee</option>
                <option value="HRO">HR Officer</option>
                <option value="USER">General User</option>
                <option value="CANDIDATE">Candidate</option>
              </select>
            </div>

            {/* Basic Information */}
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Basic Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter full name"
                  />
                </div>

                {/* Father's Name - Required for HR/HRO */}
                {['HR', 'HRO'].includes(formData.role) && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Father's Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="fatherName"
                      value={formData.fatherName}
                      onChange={handleInputChange}
                      required
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter father's name"
                    />
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Passport Number {formData.role === 'CANDIDATE' ? <span className="text-red-500">*</span> : null}
                  </label>
                  <input
                    type="text"
                    name="passportNumber"
                    value={formData.passportNumber}
                    onChange={handleInputChange}
                    required={formData.role === 'CANDIDATE'}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter passport number"
                  />
                  {formData.role !== 'CANDIDATE' && (
                    <p className="text-xs text-gray-500 mt-1">Optional for {formData.role} role</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Gender <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="gender"
                    value={formData.gender}
                    onChange={handleInputChange}
                    required
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select Gender</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Date of Birth <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    name="dateOfBirth"
                    value={formData.dateOfBirth}
                    onChange={handleInputChange}
                    required
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Blood Group
                  </label>
                  <select
                    name="bloodGroup"
                    value={formData.bloodGroup}
                    onChange={handleInputChange}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select Blood Group</option>
                    {['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'].map(bg => (
                      <option key={bg} value={bg}>{bg}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Emergency Contact Number
                  </label>
                  <input
                    type="tel"
                    name="emergencyNumber"
                    value={formData.emergencyNumber}
                    onChange={handleInputChange}
                    maxLength="10"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter emergency contact number"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Mobile Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    name="mobile"
                    value={formData.mobile}
                    onChange={handleInputChange}
                    required
                    maxLength="10"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter 10-digit mobile number"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {formData.mobile.length}/10 digits
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter email address"
                  />
                </div>

                {/* Place Field for HR/HRO */}
                {(formData.role === 'HR' || formData.role === 'HRO') && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Place/Location
                    </label>
                    <input
                      type="text"
                      name="place"
                      value={formData.place}
                      onChange={handleInputChange}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter work location (e.g., Indore, Bhopal)"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Specify the work location for this {formData.role} user
                    </p>
                  </div>
                )}

                {/* Joining Date for HR/HRO */}
                {['HR', 'HRO'].includes(formData.role) && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Joining Date <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      name="joiningDate"
                      value={formData.joiningDate}
                      onChange={handleInputChange}
                      required
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Documents Section - For HR/HRO only */}
            {['HR', 'HRO'].includes(formData.role) && (
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Documents</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Resume <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="file"
                      accept=".pdf,.doc,.docx"
                      onChange={(e) => handleFileChange(e, 'resume')}
                      required
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      PDF, DOC, DOCX (Max 5MB)
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Joining Letter <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="file"
                      accept=".pdf,.doc,.docx"
                      onChange={(e) => handleFileChange(e, 'joiningLetter')}
                      required
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Required for activation (PDF, DOC, DOCX - Max 5MB)
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Additional Documents
                    </label>
                    <input
                      type="file"
                      accept=".pdf,.doc,.docx"
                      multiple
                      onChange={(e) => handleFileChange(e, 'additionalDocs')}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Optional (Max 5 files, PDF/DOC/DOCX)
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Bank Details Section - For HR/HRO only */}
            {['HR', 'HRO'].includes(formData.role) && (
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Bank Details</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Account Holder Name
                    </label>
                    <input
                      type="text"
                      name="accountHolderName"
                      value={formData.accountHolderName}
                      onChange={handleInputChange}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter account holder name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Account Number
                    </label>
                    <input
                      type="text"
                      name="accountNumber"
                      value={formData.accountNumber}
                      onChange={handleInputChange}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter account number"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      IFSC Code
                    </label>
                    <input
                      type="text"
                      name="ifscCode"
                      value={formData.ifscCode}
                      onChange={handleInputChange}
                      maxLength="11"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter IFSC code"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Bank Name
                    </label>
                    <input
                      type="text"
                      name="bankName"
                      value={formData.bankName}
                      onChange={handleInputChange}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter bank name"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Bank Document
                    </label>
                    <input
                      type="file"
                      accept=".pdf,.doc,.docx"
                      onChange={(e) => handleFileChange(e, 'bankDocument')}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Bank passbook/statement (PDF, DOC, DOCX - Max 5MB)
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Salary Structure Section - For HR/HRO only */}
            {['HR', 'HRO'].includes(formData.role) && (
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Salary Structure</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Basic Salary
                    </label>
                    <input
                      type="number"
                      name="basic"
                      value={formData.basic}
                      onChange={handleInputChange}
                      min="0"
                      step="0.01"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter basic salary"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      DA (Dearness Allowance)
                    </label>
                    <input
                      type="number"
                      name="da"
                      value={formData.da}
                      onChange={handleInputChange}
                      min="0"
                      step="0.01"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter DA amount"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      HRA (House Rent Allowance)
                    </label>
                    <input
                      type="number"
                      name="hra"
                      value={formData.hra}
                      onChange={handleInputChange}
                      min="0"
                      step="0.01"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter HRA amount"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Special Allowance
                    </label>
                    <input
                      type="number"
                      name="specialAllowance"
                      value={formData.specialAllowance}
                      onChange={handleInputChange}
                      min="0"
                      step="0.01"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter special allowance"
                    />
                  </div>
                </div>

                <h3 className="text-lg font-semibold text-gray-900 mb-4 mt-6">Deductions</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      PF (Provident Fund)
                    </label>
                    <input
                      type="number"
                      name="pf"
                      value={formData.pf}
                      onChange={handleInputChange}
                      min="0"
                      step="0.01"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter PF amount"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      TDS (Tax Deducted at Source)
                    </label>
                    <input
                      type="number"
                      name="tds"
                      value={formData.tds}
                      onChange={handleInputChange}
                      min="0"
                      step="0.01"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter TDS amount"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      ESI (Employee State Insurance)
                    </label>
                    <input
                      type="number"
                      name="esi"
                      value={formData.esi}
                      onChange={handleInputChange}
                      min="0"
                      step="0.01"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter ESI amount"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Professional Tax
                    </label>
                    <input
                      type="number"
                      name="professionalTax"
                      value={formData.professionalTax}
                      onChange={handleInputChange}
                      min="0"
                      step="0.01"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter professional tax"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Other Deduction
                    </label>
                    <input
                      type="number"
                      name="otherDeduction"
                      value={formData.otherDeduction}
                      onChange={handleInputChange}
                      min="0"
                      step="0.01"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter other deduction amount"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Other Deduction Description
                    </label>
                    <input
                      type="text"
                      name="otherDeductionDescription"
                      value={formData.otherDeductionDescription}
                      onChange={handleInputChange}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Describe other deduction (optional)"
                    />
                  </div>
                </div>

                {/* Salary Preview */}
                {salaryPreview && (
                  <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                    <h3 className="text-lg font-medium text-blue-900 mb-3">Salary Calculation Preview</h3>
                    
                    {/* Salary Components */}
                    <div className="mb-4">
                      <h4 className="text-md font-medium text-blue-800 mb-2">Salary Components</h4>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <span className="text-blue-700">Basic:</span>
                          <span className="font-medium ml-2">₹{salaryPreview.basic?.toLocaleString()}</span>
                        </div>
                        <div>
                          <span className="text-blue-700">DA:</span>
                          <span className="font-medium ml-2">₹{salaryPreview.da?.toLocaleString()}</span>
                        </div>
                        <div>
                          <span className="text-blue-700">HRA:</span>
                          <span className="font-medium ml-2">₹{salaryPreview.hra?.toLocaleString()}</span>
                        </div>
                        <div>
                          <span className="text-blue-700">Special:</span>
                          <span className="font-medium ml-2">₹{salaryPreview.specialAllowance?.toLocaleString()}</span>
                        </div>
                      </div>
                    </div>

                    {/* Deductions */}
                    {salaryPreview.deductions && (
                      <div className="mb-4">
                        <h4 className="text-md font-medium text-red-800 mb-2">Deductions</h4>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                          <div>
                            <span className="text-red-700">PF:</span>
                            <span className="font-medium ml-2">₹{salaryPreview.deductions.pf?.toLocaleString()}</span>
                          </div>
                          <div>
                            <span className="text-red-700">TDS:</span>
                            <span className="font-medium ml-2">₹{salaryPreview.deductions.tds?.toLocaleString()}</span>
                          </div>
                          <div>
                            <span className="text-red-700">ESI:</span>
                            <span className="font-medium ml-2">₹{salaryPreview.deductions.esi?.toLocaleString()}</span>
                          </div>
                          <div>
                            <span className="text-red-700">Prof. Tax:</span>
                            <span className="font-medium ml-2">₹{salaryPreview.deductions.professionalTax?.toLocaleString()}</span>
                          </div>
                          <div>
                            <span className="text-red-700">Other:</span>
                            <span className="font-medium ml-2">₹{salaryPreview.deductions.other?.toLocaleString()}</span>
                          </div>
                          <div>
                            <span className="text-red-700">Total Deductions:</span>
                            <span className="font-bold ml-2">₹{salaryPreview.deductions.totalDeductions?.toLocaleString()}</span>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Summary */}
                    <div className="border-t pt-4">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                        <div>
                          <span className="text-blue-700">Gross Salary:</span>
                          <span className="font-bold ml-2 text-lg">₹{salaryPreview.gross?.toLocaleString()}</span>
                        </div>
                        <div>
                          <span className="text-blue-700">Total Salary:</span>
                          <span className="font-bold ml-2 text-lg">₹{salaryPreview.total?.toLocaleString()}</span>
                        </div>
                        <div>
                          <span className="text-green-700">Net Salary:</span>
                          <span className="font-bold ml-2 text-xl text-green-600">₹{salaryPreview.netSalary?.toLocaleString()}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Location Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  State
                </label>
                <select
                  name="state"
                  value={formData.state}
                  onChange={handleInputChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select State</option>
                  {STATES.map(state => (
                    <option key={state} value={state}>{state}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  District
                </label>
                <select
                  name="district"
                  value={formData.district}
                  onChange={handleInputChange}
                  disabled={!formData.state}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                >
                  <option value="">Select District</option>
                  {availableDistricts.map(district => (
                    <option key={district} value={district}>{district}</option>
                  ))}
                </select>
                {!formData.state && (
                  <p className="text-xs text-gray-500 mt-1">Please select a state first</p>
                )}
              </div>
            </div>

            {/* Professional Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">Professional Information (Optional)</h3>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Current Job Role
                </label>
                <input
                  type="text"
                  name="currentJobRole"
                  value={formData.currentJobRole}
                  onChange={handleInputChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder={['HR', 'HRO'].includes(formData.role) ? `Default: ${formData.role === 'HR' ? 'Human Resource Representative' : 'HR Officer'}` : 'Enter current job role'}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Work Experience
                </label>
                <textarea
                  name="workExperience"
                  value={formData.workExperience}
                  onChange={handleInputChange}
                  rows={3}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Describe work experience"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Education
                </label>
                <input
                  type="text"
                  name="education"
                  value={formData.education}
                  onChange={handleInputChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter education details"
                />
              </div>
            </div>

            {/* Form Actions */}
            <div className="flex gap-4 pt-6 border-t">
              <button
                type="submit"
                disabled={loading}
                className="bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? 'Creating...' : `Create ${formData.role} User`}
              </button>
              
              <button
                type="button"
                onClick={resetForm}
                disabled={loading}
                className="bg-gray-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-gray-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
              >
                Reset Form
              </button>
              
              <button
                type="button"
                onClick={() => navigate('/admin/users')}
                disabled={loading}
                className="bg-green-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
              >
                View All Users
              </button>
            </div>
          </form>
        </div>
      </div>
    </MainLayout>
  );
};

export default CreateUserPage;